import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Avatar, Box, Button, Chip, Dialog, DialogContent, DialogTitle,
    Divider, IconButton, Stack, TextField, Tooltip, Typography,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import AttachFileRoundedIcon from "@mui/icons-material/AttachFileRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";

import { channelApi } from "../api/channelApi";
import { notify } from "../../../util/toast";
import { getApiErrorMessage } from "../../../util/apiFeedback";
import { confirm as confirmDialog } from "../../../util/confirmDialog";
import { applyRangeEndChange, applyRangeStartChange } from "../../../util/dateUtils";
import ActionButton from "../../../component/common/ActionButton";
import AppAutocomplete from "../../../component/common/AppAutocomplete";
import { STATUS_LABELS, SELECTABLE_STATUSES, allowedStatuses, StatusChip, AttachmentChip } from "./taskShared";
import { useChannelReadOnly } from "../readOnly";

const asArray = (r, key) => (Array.isArray(r) ? r : (r?.[key] ?? r?.data ?? []));

function fmtTime(iso) {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleString("ko-KR", {
            year: "2-digit", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit",
        });
    } catch { return String(iso); }
}

function PersonLine({ label, person }) {
    return (
        <Stack direction="row" alignItems="center" spacing={0.75}>
            <Typography sx={{ fontSize: 11, color: "text.secondary", fontWeight: 700, minWidth: 42 }}>{label}</Typography>
            {person ? (
                <>
                    <Avatar sx={{ width: 20, height: 20, fontSize: 10, bgcolor: label === "지시자" ? "#7c3aed" : "#2563eb" }}>
                        {person.name?.[0]}
                    </Avatar>
                    <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{person.name}</Typography>
                    {(person.deptName || person.positionName) && (
                        <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
                            · {[person.deptName, person.positionName].filter(Boolean).join(" ")}
                        </Typography>
                    )}
                </>
            ) : (
                <Typography sx={{ fontSize: 12, color: "text.disabled" }}>미지정</Typography>
            )}
        </Stack>
    );
}

/**
 * 업무 상세 모달 — 지시/피드백 워크플로우.
 *  - 지시자(createdBy): 상태 전체 변경 + 설정(제목·설명·담당자·기한) 편집.
 *  - 담당자(assignee): 시작·진행 중·검토 요청까지 상태 변경 + 파일 업로드 + 피드백.
 *  - 지시자·담당자 양방향 피드백 스레드.
 */
export default function TaskDetailModal({ open, onClose, task, channelId, members, myId, onChanged }) {
    const [attachments, setAttachments] = useState([]);
    const [comments, setComments] = useState([]);
    const [commentInput, setCommentInput] = useState("");
    const [editing, setEditing] = useState(false);
    const [form, setForm] = useState(null);
    const [busy, setBusy] = useState(false);
    const fileRef = useRef();

    /**
     * 보관 채널이면 읽기 전용.
     *
     * <p>아래 두 권한 값에 <b>접어 넣는다</b> — 이 모달의 쓰기 UI(설정 수정·상태 변경·업로드·피드백·삭제)가
     * 전부 {@code canConfig}/{@code canParticipate}/{@code statusOptions} 셋 중 하나를 보고 그려지므로,
     * 각 버튼마다 조건을 덧붙이는 것보다 여기 한 곳에서 끄는 편이 빠뜨릴 자리가 없다.
     */
    const readOnly = useChannelReadOnly();

    const isCreator = !!task?.createdBy && task.createdBy.id === myId;
    const isAssignee = !!task?.assignee && task.assignee.id === myId;
    const legacyNoCreator = !!task && !task.createdBy;
    // 설정 수정·업무 삭제는 "지시자(createdBy)만". 레거시 예외 없음 — 지시자 아닌 사람은 절대 불가.
    const canConfig = isCreator && !readOnly;
    const canParticipate = (isCreator || isAssignee || legacyNoCreator) && !readOnly;
    const statusOptions = useMemo(
        () => (readOnly ? [] : allowedStatuses({ isCreator, isAssignee, legacyNoCreator })),
        [readOnly, isCreator, isAssignee, legacyNoCreator]
    );

    const refreshComments = useCallback(async () => {
        if (!task) return;
        try { setComments(asArray(await channelApi.getTaskComments(task.id), "comments")); } catch { /* noop */ }
    }, [task]);

    const refreshAttachments = useCallback(async () => {
        if (!task) return;
        try { setAttachments(asArray(await channelApi.getTaskAttachments(task.id), "attachments")); } catch { /* noop */ }
    }, [task]);

    useEffect(() => {
        if (!open || !task) return;
        setEditing(false);
        setCommentInput("");
        refreshAttachments();
        refreshComments();
    }, [open, task?.id, refreshAttachments, refreshComments]);

    if (!task) return null;

    const handleStatus = async (next) => {
        if (busy || next === task.status) return;
        setBusy(true);
        try {
            await channelApi.changeTaskStatus(task.id, next);
            await onChanged?.();
        } catch (e) {
            notify(getApiErrorMessage(e, "상태 변경 실패"), "error");
        } finally { setBusy(false); }
    };

    const handleUpload = async (e) => {
        const files = Array.from(e.target.files || []);
        e.target.value = "";
        if (!files.length) return;
        setBusy(true);
        try {
            for (const f of files) await channelApi.uploadFile(channelId, f, null, null, task.id);
            await refreshAttachments();
            notify("파일이 첨부되었습니다", "success");
        } catch (err) {
            notify(getApiErrorMessage(err, "파일 업로드 실패"), "error");
        } finally { setBusy(false); }
    };

    const handleDeleteAttachment = async (id) => {
        if (!(await confirmDialog({ message: "첨부파일을 삭제하시겠습니까?" }))) return;
        try {
            await channelApi.deleteAttachment(id);
            setAttachments(prev => prev.filter(a => a.id !== id));
        } catch (e) { notify(getApiErrorMessage(e, "파일 삭제 실패"), "error"); }
    };

    const handleAddComment = async () => {
        const content = commentInput.trim();
        if (!content || busy) return;
        setBusy(true);
        try {
            await channelApi.addTaskComment(task.id, content);
            setCommentInput("");
            await refreshComments();
        } catch (e) {
            notify(getApiErrorMessage(e, "피드백 작성 실패"), "error");
        } finally { setBusy(false); }
    };

    const handleDeleteComment = async (id) => {
        if (!(await confirmDialog({ message: "피드백을 삭제하시겠습니까?" }))) return;
        try {
            await channelApi.deleteTaskComment(id);
            setComments(prev => prev.filter(c => c.id !== id));
        } catch (e) { notify(getApiErrorMessage(e, "피드백 삭제 실패"), "error"); }
    };

    const startEdit = () => {
        setForm({
            title: task.title ?? "",
            description: task.description ?? "",
            assigneeId: task.assignee?.id ?? "",
            startDate: task.startDate ?? "",
            endDate: task.endDate ?? "",
        });
        setEditing(true);
    };

    const handleDeleteTask = async () => {
        if (!(await confirmDialog({ message: "업무를 삭제하시겠습니까? 첨부·피드백도 함께 삭제됩니다." }))) return;
        setBusy(true);
        try {
            await channelApi.deleteTask(task.id);
            onClose?.();
            await onChanged?.();
            notify("업무가 삭제되었습니다", "success");
        } catch (e) {
            notify(getApiErrorMessage(e, "업무 삭제 실패"), "error");
        } finally { setBusy(false); }
    };

    const handleSaveConfig = async () => {
        if (!form.title.trim()) { notify("업무명을 입력하세요", "error"); return; }
        setBusy(true);
        try {
            await channelApi.updateTask(task.id, {
                title: form.title,
                description: form.description || null,
                assigneeId: form.assigneeId || null,
                startDate: form.startDate || null,
                endDate: form.endDate || null,
            });
            setEditing(false);
            await onChanged?.();
        } catch (e) {
            notify(getApiErrorMessage(e, "업무 설정 수정 실패"), "error");
        } finally { setBusy(false); }
    };

    const memberEmployees = (members || []).map(m => m.employee).filter(Boolean);
    const roleLabel = isCreator ? "지시자" : isAssignee ? "담당자" : null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
            PaperProps={{ sx: { borderRadius: 2 } }}>
            <DialogTitle sx={{ pr: 6 }}>
                <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                    <StatusChip status={task.status} />
                    <Typography sx={{ fontWeight: 800, fontSize: 17 }}>{task.title}</Typography>
                    {/*{roleLabel && (*/}
                    {/*    <Chip label={`나: ${roleLabel}`} size="small"*/}
                    {/*        sx={{ fontSize: 11, fontWeight: 700, bgcolor: "#eef2ff", color: "#4338ca" }} />*/}
                    {/*)}*/}
                </Stack>
                <IconButton onClick={onClose} sx={{ position: "absolute", top: 12, right: 12 }}>
                    <CloseRoundedIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers sx={{ p: 2.5 }}>
                <Stack spacing={2.25}>
                    {/* 사람 · 기한 */}
                    <Stack spacing={0.75}>
                        <PersonLine label="지시자" person={task.createdBy} />
                        <PersonLine label="담당자" person={task.assignee} />
                        {(task.startDate || task.endDate) && (
                            <Stack direction="row" alignItems="center" spacing={0.75}>
                                <Typography sx={{ fontSize: 11, color: "text.secondary", fontWeight: 700, minWidth: 42 }}>기한</Typography>
                                <Typography sx={{ fontSize: 12 }}>{task.startDate || "미정"} ~ {task.endDate || "미정"}</Typography>
                            </Stack>
                        )}
                    </Stack>

                    {task.description && !editing && (
                        <Box sx={{ bgcolor: "#f8fafc", border: "1px solid #eef2f7", borderRadius: 1.5, p: 1.5 }}>
                            <Typography sx={{ fontSize: 13, whiteSpace: "pre-wrap" }}>{task.description}</Typography>
                        </Box>
                    )}

                    {/* 설정 편집 (지시자) */}
                    {editing ? (
                        <Box sx={{ border: "1px solid #2563eb44", borderRadius: 1.5, p: 1.75 }}>
                            <Typography sx={{ fontWeight: 700, fontSize: 13, mb: 1.25 }}>업무 설정 수정</Typography>
                            <Stack spacing={1.25}>
                                <TextField label="업무명" size="small" value={form.title} fullWidth
                                    onChange={e => setForm(p => ({ ...p, title: e.target.value }))} />
                                <Box>
                                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: "text.secondary", mb: 0.5 }}>담당자</Typography>
                                    <AppAutocomplete
                                        placeholder="이름·부서 검색"
                                        options={memberEmployees}
                                        value={memberEmployees.find(emp => emp?.id === Number(form.assigneeId)) ?? null}
                                        onChange={(v) => setForm(p => ({ ...p, assigneeId: v?.id ?? "" }))}
                                        getOptionLabel={(o) => o?.name || ""}
                                        isOptionEqualToValue={(a, b) => a?.id === b?.id}
                                        secondaryText={(o) => [o?.deptName, o?.positionName].filter(Boolean).join(" · ")}
                                    />
                                </Box>
                                <Stack direction="row" spacing={1}>
                                    <TextField label="시작일" type="date" size="small" value={form.startDate}
                                        onChange={e => setForm(p => applyRangeStartChange(p, e.target.value, { startKey: "startDate", endKey: "endDate", isDate: true }))}
                                        InputLabelProps={{ shrink: true }} sx={{ flex: 1 }} />
                                    <TextField label="마감일" type="date" size="small" value={form.endDate}
                                        onChange={e => setForm(p => applyRangeEndChange(p, e.target.value, { startKey: "startDate", endKey: "endDate" }))}
                                        InputLabelProps={{ shrink: true }} sx={{ flex: 1 }} />
                                </Stack>
                                <TextField label="설명" size="small" value={form.description} multiline rows={2} fullWidth
                                    onChange={e => setForm(p => ({ ...p, description: e.target.value }))} />
                                <Stack direction="row" spacing={1} justifyContent="flex-end">
                                    <ActionButton variant="cancel" onClick={() => setEditing(false)} disabled={busy} />
                                    <ActionButton variant="save" startIcon={false} onClick={handleSaveConfig} disabled={busy} />
                                </Stack>
                            </Stack>
                        </Box>
                    ) : canConfig && (
                        <Box>
                            <Button size="small" startIcon={<EditRoundedIcon sx={{ fontSize: 15 }} />}
                                onClick={startEdit}
                                sx={{ color: "#64748b", textTransform: "none", fontSize: 12 }}>
                                설정 수정 (제목·담당자·기한)
                            </Button>
                        </Box>
                    )}

                    <Divider />

                    {/* 상태 변경 */}
                    <Box>
                        <Typography sx={{ fontWeight: 700, fontSize: 13, mb: 0.75 }}>상태</Typography>
                        {statusOptions.length === 0 ? (
                            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                                {readOnly
                                    ? "보관된 채널이라 상태를 변경할 수 없습니다."
                                    : "이 업무의 지시자 또는 담당자만 상태를 변경할 수 있습니다."}
                            </Typography>
                        ) : (
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                {SELECTABLE_STATUSES.map(st => {
                                    const meta = STATUS_LABELS[st];
                                    const active = task.status === st;
                                    const allowed = statusOptions.includes(st);
                                    return (
                                        <Button key={st} size="small" disableElevation
                                            variant={active ? "contained" : "outlined"}
                                            disabled={!allowed || busy || active}
                                            onClick={() => handleStatus(st)}
                                            sx={{
                                                textTransform: "none", fontWeight: 700, fontSize: 12, borderRadius: 5, px: 1.5,
                                                ...(active
                                                    ? { bgcolor: meta.color, "&:hover": { bgcolor: meta.color }, color: "#fff" }
                                                    : { color: meta.color, borderColor: `${meta.color}66` }),
                                            }}>
                                            {meta.label}
                                        </Button>
                                    );
                                })}
                            </Stack>
                        )}
                        {isAssignee && !isCreator && (
                            <Typography sx={{ fontSize: 11.5, color: "text.secondary", mt: 0.75 }}>
                                작업을 마치면 <b>검토 요청</b>으로 제출하세요. 완료 처리는 지시자가 승인합니다.
                            </Typography>
                        )}
                        {isCreator && task.status === "REVIEW_REQUEST" && (
                            <Typography sx={{ fontSize: 11.5, color: "#b45309", mt: 0.75, fontWeight: 700 }}>
                                담당자가 검토를 요청했습니다 — <b>완료</b>(승인) 또는 <b>진행 중</b>(반려)으로 변경하세요.
                            </Typography>
                        )}
                    </Box>

                    <Divider />

                    {/* 파일 */}
                    <Box>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                            <Typography sx={{ fontWeight: 700, fontSize: 13 }}>첨부 파일 ({attachments.length})</Typography>
                            {canParticipate && (
                                <>
                                    <Button size="small" startIcon={<AttachFileRoundedIcon sx={{ fontSize: 15 }} />}
                                        onClick={() => fileRef.current?.click()} disabled={busy}
                                        sx={{ color: "#64748b", textTransform: "none", fontSize: 12 }}>
                                        파일 업로드
                                    </Button>
                                    <input ref={fileRef} type="file" hidden multiple onChange={handleUpload} />
                                </>
                            )}
                        </Stack>
                        {attachments.length === 0 ? (
                            <Typography sx={{ fontSize: 12, color: "text.disabled" }}>첨부된 파일이 없습니다.</Typography>
                        ) : (
                            <Stack direction="row" flexWrap="wrap" gap={0.75}>
                                {attachments.map(a => (
                                    <AttachmentChip key={a.id} attachment={a}
                                        editable={canParticipate}
                                        onDelete={canParticipate ? () => handleDeleteAttachment(a.id) : undefined} />
                                ))}
                            </Stack>
                        )}
                    </Box>

                    <Divider />

                    {/* 피드백 스레드 */}
                    <Box>
                        <Typography sx={{ fontWeight: 700, fontSize: 13, mb: 1 }}>피드백 ({comments.length})</Typography>
                        <Stack spacing={1.25}>
                            {comments.length === 0 && (
                                <Typography sx={{ fontSize: 12, color: "text.disabled" }}>아직 피드백이 없습니다.</Typography>
                            )}
                            {comments.map(c => {
                                const mine = c.author?.id === myId;
                                const canDelete = (mine || isCreator) && !readOnly;
                                return (
                                    <Stack key={c.id} direction="row" spacing={1} alignItems="flex-start">
                                        <Avatar sx={{ width: 26, height: 26, fontSize: 11, bgcolor: mine ? "#2563eb" : "#94a3b8" }}>
                                            {c.author?.name?.[0]}
                                        </Avatar>
                                        <Box flex={1} minWidth={0} sx={{ bgcolor: "#f8fafc", border: "1px solid #eef2f7", borderRadius: 1.5, p: 1.25 }}>
                                            <Stack direction="row" alignItems="center" spacing={0.75} mb={0.25}>
                                                <Typography sx={{ fontSize: 12, fontWeight: 700 }}>{c.author?.name || "-"}</Typography>
                                                <Typography sx={{ fontSize: 10.5, color: "text.secondary" }}>{fmtTime(c.createdAt)}</Typography>
                                                {canDelete && (
                                                    <Tooltip title="삭제">
                                                        <IconButton size="small" onClick={() => handleDeleteComment(c.id)} sx={{ ml: "auto", p: 0.25 }}>
                                                            <DeleteRoundedIcon sx={{ fontSize: 13, color: "#cbd5e1" }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Stack>
                                            <Typography sx={{ fontSize: 13, whiteSpace: "pre-wrap" }}>{c.content}</Typography>
                                        </Box>
                                    </Stack>
                                );
                            })}
                        </Stack>

                        {canParticipate ? (
                            <Stack direction="row" spacing={1} alignItems="flex-end" mt={1.5}>
                                <TextField
                                    value={commentInput}
                                    onChange={e => setCommentInput(e.target.value)}
                                    onKeyDown={e => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); handleAddComment(); } }}
                                    placeholder="피드백을 입력하세요 (Ctrl/⌘+Enter 전송)"
                                    size="small" fullWidth multiline maxRows={4} />
                                <IconButton onClick={handleAddComment} disabled={busy || !commentInput.trim()}
                                    sx={{ bgcolor: "#2563eb", color: "#fff", "&:hover": { bgcolor: "#1d4ed8" }, "&.Mui-disabled": { bgcolor: "#e2e8f0" } }}>
                                    <SendRoundedIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                            </Stack>
                        ) : (
                            <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 1 }}>
                                {readOnly
                                    ? "보관된 채널이라 피드백을 작성할 수 없습니다."
                                    : "지시자 또는 담당자만 피드백을 작성할 수 있습니다."}
                            </Typography>
                        )}
                    </Box>

                    {canConfig && (
                        <>
                            <Divider />
                            <Box>
                                <ActionButton size="small" variant="close"
                                    onClick={handleDeleteTask} disabled={busy}
                                    sx={{ textTransform: "none", fontSize: 12 }}>
                                    업무 삭제
                                </ActionButton>
                            </Box>
                        </>
                    )}
                </Stack>
            </DialogContent>
        </Dialog>
    );
}
